import React from 'react';
import type { AdminAccountRole, AdminPermissions } from '@business-game/shared';
import {
  ADMIN_ACTION_LABELS,
  ADMIN_ROLE_LABELS,
  ADMIN_SECTION_LABELS,
  EMPTY_OPERATOR_PERMISSIONS,
  FACILITATOR_PERMISSIONS,
  SECTION_ACTIONS,
  SECTION_VIEW_ACTION,
} from '@business-game/shared';
import type { GameSession } from '@business-game/shared';

type Props = {
  role: AdminAccountRole;
  permissions: AdminPermissions;
  facilitatorName: string;
  assignedSessionIds: number[];
  sessions: GameSession[];
  canManageRoles: boolean;
  /** Masque le champ libre « Nom facilitateur » quand le parent gère le lien au facilitateur. */
  hideFacilitatorName?: boolean;
  onRoleChange: (role: AdminAccountRole) => void;
  onPermissionsChange: (permissions: AdminPermissions) => void;
  onFacilitatorNameChange: (value: string) => void;
  onAssignedSessionIdsChange: (ids: number[]) => void;
};

export const AdminUserPermissionsEditor: React.FC<Props> = ({
  role,
  permissions,
  facilitatorName,
  assignedSessionIds,
  sessions,
  canManageRoles,
  hideFacilitatorName = false,
  onRoleChange,
  onPermissionsChange,
  onFacilitatorNameChange,
  onAssignedSessionIdsChange,
}) => {
  const setSection = (key: keyof AdminPermissions['sections'], value: boolean) => {
    const nextActions = { ...permissions.actions };
    const relatedActions = SECTION_ACTIONS[key] ?? [];

    if (!value) {
      relatedActions.forEach((action) => {
        nextActions[action] = false;
      });
    } else {
      const viewAction = SECTION_VIEW_ACTION[key];
      if (viewAction) {
        nextActions[viewAction] = true;
      }
    }

    onPermissionsChange({
      ...permissions,
      sections: { ...permissions.sections, [key]: value },
      actions: nextActions,
    });
  };

  const setAction = (key: keyof AdminPermissions['actions'], value: boolean) => {
    const nextActions = { ...permissions.actions, [key]: value };

    if (!value && key === 'sessionsView') {
      (SECTION_ACTIONS.sessions ?? []).forEach((action) => {
        nextActions[action] = false;
      });
    }

    if (!value && key === 'programsView') {
      (SECTION_ACTIONS.programs ?? []).forEach((action) => {
        nextActions[action] = false;
      });
    }

    if (value && key.startsWith('sessions') && key !== 'sessionsView') {
      nextActions.sessionsView = true;
    }

    if (value && key.startsWith('programs') && key !== 'programsView') {
      nextActions.programsView = true;
    }

    onPermissionsChange({
      ...permissions,
      actions: nextActions,
    });
  };

  const isActionEnabled = (key: keyof AdminPermissions['actions']): boolean => {
    if (key.startsWith('sessions') && key !== 'sessionsView') {
      return permissions.sections.sessions;
    }
    if (key.startsWith('programs') && key !== 'programsView') {
      return permissions.sections.programs;
    }
    if (key === 'contentEdit') {
      return (
        permissions.sections.criterias ||
        permissions.sections.impacts ||
        permissions.sections.verbatims ||
        permissions.sections.playerTexts
      );
    }
    return true;
  };

  const toggleSession = (sessionId: number) => {
    if (assignedSessionIds.includes(sessionId)) {
      onAssignedSessionIdsChange(assignedSessionIds.filter((id) => id !== sessionId));
      return;
    }
    onAssignedSessionIdsChange([...assignedSessionIds, sessionId]);
  };

  return (
    <div className="space-y-4">
      {canManageRoles && (
        <div>
          <label className="mb-1 ml-0.5 block text-xs font-bold uppercase tracking-wider text-slate-400">Type de compte</label>
          <select
            value={role}
            onChange={(e) => {
              const nextRole = e.target.value as AdminAccountRole;
              onRoleChange(nextRole);
              if (nextRole === 'facilitator') {
                onPermissionsChange(FACILITATOR_PERMISSIONS);
              } else if (nextRole === 'operator') {
                onPermissionsChange(EMPTY_OPERATOR_PERMISSIONS);
              }
            }}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/10"
          >
            <option value="operator">{ADMIN_ROLE_LABELS.operator}</option>
            <option value="facilitator">{ADMIN_ROLE_LABELS.facilitator}</option>
            {canManageRoles && (
              <>
                <option value="admin">{ADMIN_ROLE_LABELS.admin}</option>
                <option value="super_admin">{ADMIN_ROLE_LABELS.super_admin}</option>
              </>
            )}
          </select>
        </div>
      )}

      {role === 'facilitator' && (
        <>
          {!hideFacilitatorName && (
          <div>
            <label className="mb-1 ml-0.5 block text-xs font-bold uppercase tracking-wider text-slate-400">
              Nom facilitateur
            </label>
            <input
              type="text"
              value={facilitatorName}
              onChange={(e) => onFacilitatorNameChange(e.target.value)}
              placeholder="Doit correspondre au facilitateur des sessions"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/10"
            />
          </div>
          )}

          <div>
            <div className="mb-1 ml-0.5 text-xs font-bold uppercase tracking-wider text-slate-400">Sessions assignées</div>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2">
              {sessions.map((session) => (
                <label key={session.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={assignedSessionIds.includes(session.id)}
                    onChange={() => toggleSession(session.id)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                  />
                  <span className="text-sm font-medium text-slate-800">
                    {session.name || session.code} · {session.code}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {role === 'operator' && (
        <>
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Sections visibles</div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {(Object.keys(ADMIN_SECTION_LABELS) as Array<keyof AdminPermissions['sections']>).map((key) => (
                <label key={key} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <input
                    type="checkbox"
                    checked={permissions.sections[key]}
                    onChange={(e) => setSection(key, e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                  />
                  <span className="text-sm font-medium text-slate-800">{ADMIN_SECTION_LABELS[key]}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Actions autorisées</div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {(Object.keys(ADMIN_ACTION_LABELS) as Array<keyof AdminPermissions['actions']>).map((key) => (
                <label
                  key={key}
                  className={`flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 ${
                    !isActionEnabled(key) ? 'opacity-40' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={permissions.actions[key]}
                    disabled={!isActionEnabled(key)}
                    onChange={(e) => setAction(key, e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm font-medium text-slate-800">{ADMIN_ACTION_LABELS[key]}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
